import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { AttendanceService } from 'src/shared/services/attendance.service';
import { AuthService } from 'src/shared/services/auth.service';
import { ResponseService } from 'src/shared/services/response-service.service';
import Swal from 'sweetalert2';
import { FileService } from 'src/shared/services/file.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as XLSX from 'xlsx';

type AttendanceStatus = 'present' | 'absent';

@Component({
  selector: 'app-attendance-list',
  templateUrl: './attendance-list.component.html',
  styleUrl: './attendance-list.component.scss'
})
export class AttendanceListComponent implements OnInit {

  attendanceList: any[] = [];
  private attendanceListOriginalOrder: any[] = [];
  // Statuses as loaded, keyed by participant, to tell whether anything changed
  private savedStatuses = new Map<any, any>();
  sessionDate: any;
  activityName = '';
  sessionId: number;
  periodId: number;
  columnsToDisplay = ['student_name', 'class_name', 'attendance'];
  subs: Subscription;
  totalNewParticipant: number;
  newParticipanList: any[] = [];
  loading = false;
  saving = false;
  loadExcel = false;
  previewData = [];
  isValid = false;
  hasData = false;

  @ViewChild('uploadTemplate') uploadTemplate: TemplateRef<any>;

  constructor(
    private attendanceService: AttendanceService,
    private route: ActivatedRoute,
    private router: Router,
    private rpSer: ResponseService,
    public auth: AuthService,
    private fileService: FileService,
    private modalService: NgbModal,
  ) {
    // Passed by the session list; a direct link falls back to the date from the API
    const state = this.router.getCurrentNavigation()?.extras.state || history.state || {};
    this.activityName = state.activityName || '';
    this.sessionDate = state.sessionDate ? new Date(state.sessionDate) : null;
  }

  ngOnInit() {
    this.auth.loadPermissions(true);
    this.sessionId = this.route.snapshot.params['id'];
    this.periodId = this.route.snapshot.params['period'];
    this.getAttendanceList();
  }

  get canEdit(): boolean {
    return !!this.auth.permissions['SCHOOL_PROGRAM_ATTENDANCE_EDIT'];
  }

  get presentCount(): number {
    return this.attendanceList.filter((item: any) => item.status === 'present').length;
  }

  get absentCount(): number {
    return this.attendanceList.filter((item: any) => item.status === 'absent').length;
  }

  get unmarkedCount(): number {
    return this.attendanceList.length - this.presentCount - this.absentCount;
  }

  get isDirty(): boolean {
    return this.attendanceList.some((item: any) => this.savedStatuses.get(item.activity_participant_id) !== item.status);
  }

  getAttendanceList() {
    if (this.subs) {
      this.subs.unsubscribe();
    }
    this.attendanceList = [];
    this.loading = true;
    const params = {
      period_id: +this.periodId
    }
    this.subs = this.attendanceService.getActivityAttendanceList(this.sessionId, params).subscribe({
      next: (response: any) => {
        if (response.status) {
          this.attendanceList = response.data.list;
          this.attendanceListOriginalOrder = [...this.attendanceList];
          this.savedStatuses = new Map(this.attendanceList.map((item: any) => [item.activity_participant_id, item.status]));
          this.newParticipanList = response.data.new_student;
          this.totalNewParticipant = response.data.new_student ? response.data.new_student.length : 0;
          if (!this.sessionDate) {
            this.sessionDate = this.parseApiDate(response.data.activity_session_date);
          }
        } else {
          this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
        }
        this.loading = false;
      },
      error: (error: any) => {
        this.rpSer.showAddError();
        this.loading = false;
      }
    });
  }

  // The API sends toLocaleDateString() output (M/D/YYYY on the server); keep the raw text if it isn't that
  private parseApiDate(value: string): Date | string | null {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value || '');
    return match ? new Date(+match[3], +match[1] - 1, +match[2]) : value || null;
  }

  isDateValue(value: any): boolean {
    return value instanceof Date;
  }

  toggleStatus(element: any): void {
    if (!this.canEdit || this.saving) {
      return;
    }
    element.status = element.status === 'present' ? 'absent' : 'present';
  }

  markAll(status: AttendanceStatus): void {
    if (!this.canEdit || this.saving) {
      return;
    }
    this.attendanceList.forEach((item: any) => item.status = status);
  }

  sortAttendanceList(sortEvent: Sort | Event) {
    const sort = sortEvent as Sort;
    this.attendanceList = sort.direction
      ? [...this.attendanceList].sort((first, second) => {
          const firstValue = String(first[sort.active] ?? '');
          const secondValue = String(second[sort.active] ?? '');
          const comparison = firstValue.localeCompare(secondValue, undefined, { numeric: true, sensitivity: 'base' });
          return sort.direction === 'asc' ? comparison : -comparison;
        })
      : [...this.attendanceListOriginalOrder];
  }

  onBack() {
    if (!this.isDirty) {
      this.router.navigate(['../../../'], { relativeTo: this.route });
      return;
    }
    Swal.fire({
      title: 'Discard changes?',
      text: 'Your attendance changes have not been saved.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Discard',
      cancelButtonText: 'Keep editing'
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(['../../../'], { relativeTo: this.route });
      }
    });
  }

  onNewEntry() {
    const params = {
      activity_attendance_id: this.sessionId
    }
    this.attendanceService.newEntrySessionAttendance(params).subscribe({
      next: (response: any) => {
        if (response.status) {
          // this.dataAttendance = response.data;
          this.getAttendanceList();
        } else {
          this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
        }
      },
      error: (error: any) => {
        this.rpSer.showAddError();
      }
    });
  }

  onSaveAttendance() {
    if (this.saving) {
      return;
    }
    // A student without a status would be saved as null
    if (this.unmarkedCount > 0) {
      Swal.fire({
        title: 'Some students are not marked',
        text: `Mark ${this.unmarkedCount} more student(s) as present or absent before saving.`,
        icon: 'warning'
      });
      return;
    }
    const params = {
      list: this.attendanceList,
      new: this.newParticipanList
    };
    this.saving = true;
    this.subs = this.attendanceService.saveAttendanceData(this.sessionId, params).subscribe({
      next: (response: any) => {
        this.saving = false;
        if (response.status) {
          this.rpSer.showAddSuccess('Attendance saved successfully!');
          this.savedStatuses = new Map(this.attendanceList.map((item: any) => [item.activity_participant_id, item.status]));
          this.router.navigate(['../../../'], { relativeTo: this.route });
        } else {
          this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
        }
      },
      error: (error: any) => {
        this.saving = false;
        this.rpSer.showAddError();
      }
    });
  }

  // ── Excel template / upload (from master) ──

  onDownloadTemplate() {
    this.attendanceService.attendanceTemplate(this.sessionId).subscribe({
      next: (response: Blob) => {
        if (response) {
          const blobUrl = URL.createObjectURL(response as any);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `${'Upload Student Attendance'}.xlsx`;
          link.click();
          URL.revokeObjectURL(blobUrl);
        } else {
          this.rpSer.showAddError(response ? response as any : 'An unexpected error occurred. Please check your connection and try again.');
        }
      },
      error: (error) => {
        this.rpSer.showAddError(error ? error : 'An unexpected error occurred. Please check your connection and try again.');
      }
    });
  }

  async openWindow() {
    this.modalService.open(this.uploadTemplate, {
      ariaLabelledBy: 'modal-basic-title',
      keyboard: false,
      size: 'lg'
    }).result.then((result: any) => {

    }, (reason: any) => {

    });
  }

  onUploadExcel() {
    this.openWindow();
  }

  async uploadExcel(item: any) {
    this.loadExcel = true;

    const result: any[] = [];

    if (item.addedFiles.length === 0) {
      this.rpSer.showAddError('No file selected');
      this.loadExcel = false;
      return;
    }

    // Validate File Type
    if (!await this.fileService.validateFiles(item.addedFiles,
      ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ])) {
      item.addedFiles = [];
      this.rpSer.showAddError('Invalid file type');
      this.loadExcel = false;
      return;
    }

    //validate size
    if (item.addedFiles[0].size > 5 * 1024 * 1025) {
      item.addedFiles = [];
      this.rpSer.showAddError('File size too large');
      this.loadExcel = false;
      return;
    }

    const file = item.addedFiles[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onload = async (e: any) => {
      const binaryStr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(binaryStr, { type: 'binary' });
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      const data = XLSX.utils.sheet_to_json(ws, {
        range: 4,
        header: [
          'activity_participant_id',
          'student_name',
          'status',
        ],
        defval: '',
      });
      result.push(...data);
    };

    reader.onloadend = () => {
      this.excelCreateData(result)
        .then(() => {
          this.loadExcel = false;
          // show the uploaded statuses
          this.modalService.dismissAll();
          this.getAttendanceList();
        }, () => {
          this.loadExcel = false;
        });
    };

    this.loadExcel = false;
  }

  excelCreateData(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const param = {
        sessionId: this.sessionId,
        data: data
      }
      this.attendanceService.uploadAttendance(param)
        .subscribe((response: any) => {
          if (response.status) {
            this.previewData = response.data.data;
            this.isValid = response.data.valid;
            this.hasData = true;
            this.rpSer.showAddSuccess(response.message ? response.message : 'Success');
            resolve(true);
          } else {
            this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
            reject();
          }
        }, (error: any) => {
          this.rpSer.showAddError(error ? error : 'An unexpected error occurred. Please check your connection and try again.');
          reject();
        });
    });
  }

}
